import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import TablePaginationSection from '../../Shared/Components/DataTable/TablePaginationSection';
import { useUninvoicedReservationPaymentsReportPage } from '../Hooks/useUninvoicedReservationPaymentsReportPage';
import { useGetReportsCompaniesLookupQuery } from '../Service/reportsCompaniesLookupApi';
import type { BookingUninvoicedReservationPaymentsReportRowDto } from '../Types/reports.types';

const fmt = (amount: number) =>
  amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const UninvoicedReservationPaymentsReportPage: React.FC = () => {
  const {
    data,
    isLoading,
    isFetching,
    queryErrorMessage,
    validationError,
    draftFilters,
    filters,
    hasCompanyId,
    expandedBookingIds,
    handleDraftFilterChange,
    handleSearchSubmit,
    handleResetFilters,
    handlePageChange,
    handlePageSizeChange,
    toggleExpandedBooking,
    goToBookingEdit,
  } = useUninvoicedReservationPaymentsReportPage();

  const { companies } = useGetReportsCompaniesLookupQuery();

  const pageLocalNetTotal = React.useMemo(
    () => (data?.items ?? []).reduce((sum, item) => sum + item.netTotal, 0),
    [data]
  );

  return (
    <Stack spacing={2}>
      <CustomCard
        icon={<ReceiptIcon fontSize="large" color="primary" />}
        title="Wpłaty bez faktury"
        subheader="Rezerwacje z wpłatami bank/KP bez wystawionej faktury"
        sx={{ mb: 4 }}
      >
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearchSubmit();
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="company-select-label">Firma</InputLabel>
                <Select
                  labelId="company-select-label"
                  label="Firma"
                  value={draftFilters.companyId ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleDraftFilterChange({ companyId: value ? Number(value) : undefined });
                  }}
                  data-testid="company-select"
                >
                  <MenuItem value="">Wybierz firmę</MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={String(company.id)} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Zakończenie od"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.bookingDateToFrom ?? ''}
                onChange={(e) =>
                  handleDraftFilterChange({ bookingDateToFrom: e.target.value || undefined })
                }
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Zakończenie do"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={draftFilters.bookingDateToTo ?? ''}
                onChange={(e) =>
                  handleDraftFilterChange({ bookingDateToTo: e.target.value || undefined })
                }
              />
            </Grid>

            {validationError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{validationError}</Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={<SearchIcon />}
                  data-testid="search-submit-btn"
                >
                  Wyszukaj
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  type="button"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleResetFilters}
                  data-testid="reset-filters-btn"
                >
                  Wyczyść filtry
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CustomCard>

      {!hasCompanyId && (
        <Typography color="text.secondary" data-testid="no-company-state">
          Wybierz firmę, aby wyświetlić raport.
        </Typography>
      )}

      {hasCompanyId && queryErrorMessage && (
        <Alert severity="error" data-testid="error-state">
          {queryErrorMessage}
        </Alert>
      )}

      {hasCompanyId && (isLoading || isFetching) && !queryErrorMessage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }} data-testid="loading-state">
          <CircularProgress />
        </Box>
      )}

      {hasCompanyId && !isLoading && !queryErrorMessage && data?.items.length === 0 && (
        <Typography color="text.secondary" data-testid="empty-state">
          Brak rezerwacji z wpłatami bez wystawionej faktury dla wybranych filtrów.
        </Typography>
      )}

      {hasCompanyId && !isLoading && !queryErrorMessage && data && data.items.length > 0 && (
        <Box data-testid="results-state">
          <Alert severity="info" data-testid="page-local-summary" sx={{ mb: 2 }}>
            Na tej stronie: {data.items.length} {data.items.length === 1 ? 'booking' : 'bookingów'},
            saldo netto {fmt(pageLocalNetTotal)}
          </Alert>

          {data.items.map((booking: BookingUninvoicedReservationPaymentsReportRowDto) => {
            const isBookingExpanded = expandedBookingIds.includes(booking.bookingId);

            return (
            <Accordion
              key={booking.bookingId}
              expanded={isBookingExpanded}
              onChange={() => toggleExpandedBooking(booking.bookingId)}
              data-testid={`booking-accordion-${booking.bookingId}`}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label={`${isBookingExpanded ? 'Zwiń' : 'Rozwiń'} booking ${booking.offerName}`}
                aria-controls={`booking-details-${booking.bookingId}`}
                id={`booking-header-${booking.bookingId}`}
              >
                <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography fontWeight="bold">{booking.offerName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {booking.bookingDateFrom} — {booking.bookingDateTo}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="body2">{booking.companyName}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <Typography variant="body2">Rezerwacje: {booking.reservationCount}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 2 }}>
                    <Typography variant="body2">Saldo: {fmt(booking.netTotal)}</Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails id={`booking-details-${booking.bookingId}`}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    aria-label={`Przejdź do bookingu ${booking.offerName}`}
                    onClick={() => goToBookingEdit(booking.bookingId)}
                    data-testid={`goto-booking-btn-${booking.bookingId}`}
                  >
                    Przejdź do bookingu
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small" aria-label={`Rezerwacje bookingu ${booking.offerName}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Klient</TableCell>
                        <TableCell align="right">Bank</TableCell>
                        <TableCell align="right">KP</TableCell>
                        <TableCell align="right">Zwroty</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {booking.reservations.map((reservation) => (
                        <TableRow
                          key={reservation.reservationId}
                          data-testid={`reservation-row-${reservation.reservationId}`}
                        >
                          <TableCell>{reservation.clientDisplayName}</TableCell>
                          <TableCell align="right">{fmt(reservation.bankPaymentAmount)}</TableCell>
                          <TableCell align="right">{fmt(reservation.cashKpPaymentAmount)}</TableCell>
                          <TableCell align="right">{fmt(reservation.refundAmount)}</TableCell>
                          <TableCell align="right">{fmt(reservation.netAllocatedPaymentAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
            );
          })}

          <TablePaginationSection
            totalCount={data.totalCount}
            page={(filters.pageNumber ?? 1) - 1}
            rowsPerPage={filters.pageSize ?? 10}
            onPageChange={(newPage) => handlePageChange(newPage + 1)}
            onRowsPerPageChange={handlePageSizeChange}
          />
        </Box>
      )}
    </Stack>
  );
};

export default UninvoicedReservationPaymentsReportPage;
